/**
 * @file RadioButtonGroup.js
 * Customized version of the RadioButtonGroup component in react-hook-form-mui.
 * Adds styling and a11y capabilities to the original version.
 *
 * Original version: https://github.com/dohomi/react-hook-form-mui/blob/master/packages/rhf-mui/src/RadioButtonGroup.tsx
 */
import { forwardRef, Fragment } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
  useTheme
} from '@mui/material';
import { TextFieldElement, useFormError } from 'react-hook-form-mui';
import { asterisk } from '@lib/utils';

const RadioButtonGroup = forwardRef(function RadioButtonGroup(props, ref) {
  const {
    helperText,
    options,
    label,
    name,
    parseError,
    labelKey = 'label',
    valueKey = 'id',
    required,
    emptyOptionLabel,
    returnObject,
    row,
    control,
    type,
    labelProps,
    disabled,
    formLabelProps,
    helperTextProps,
    description,
    otherTextField,
    otherTextFieldLabel,
    otherTextFieldHiddenSpace = false,
    ...rest
  } = props;
  const theme = useTheme();

  const { clearErrors } = useFormContext();

  const errorMsgFn = useFormError();
  const customErrorFn = parseError || errorMsgFn;

  const {
    field,
    fieldState: { error }
  } = useController({
    name,
    rules: required ? { required: 'This field is required' } : undefined,
    disabled,
    control
  });

  const renderHelperText = error
    ? typeof customErrorFn === 'function'
      ? customErrorFn(error)
      : error.message
    : helperText;

  const onRadioChange = event => {
    const radioValue = event.target.value;
    const returnValue = returnObject
      ? options.find(items => items[valueKey] === radioValue)
      : radioValue;
    field.onChange(returnValue);
    // Clear the other_text field error when switching away from "Other"
    if (otherTextField && radioValue !== 'Other') {
      clearErrors('other_text');
    }
    if (typeof rest.onChange === 'function') {
      rest.onChange(returnValue);
    }
  };

  return (
    <FormControl
      error={!!error}
      ref={ref}
      component="fieldset"
      sx={{
        '.MuiFormHelperText-root.Mui-error': { display: 'none' }
      }}
    >
      {label && (
        <FormLabel
          component="legend"
          sx={{ display: 'flex' }}
          id={`${name}.label`}
          {...formLabelProps}
          required={required}
          error={!!error}
        >
          {label}
        </FormLabel>
      )}
      {description && <Typography variant="body2" component="div">{description(error)}</Typography>}
      <RadioGroup
        onChange={onRadioChange}
        name={name}
        row={row}
        value={field.value || ''}
        aria-labelledby={`${name}.label`}
      >
        {emptyOptionLabel && (
          <FormControlLabel
            {...labelProps}
            control={
              <Radio
                sx={{
                  color: error ? theme.palette.error.main : undefined
                }}
                checked={!field.value}
              />
            }
            label={emptyOptionLabel}
            value=""
          />
        )}
        {options.map(option => {
          const optionKey = option[valueKey];
          if (!optionKey) {
            console.error(
              `RadioButtonGroup: valueKey ${valueKey} does not exist on option`,
              option
            );
          }
          let val = returnObject ? field.value?.[valueKey] : field.value;
          if (type === 'number') {
            val = Number(val);
          }
          const isChecked = val === optionKey;
          const fieldId = `${name}.${String(valueKey).replace(' ', '-')}`;
          const isOther = optionKey === 'Other';
          const otherTextLabel = otherTextFieldLabel ? otherTextFieldLabel.replaceAll(/(?!<>)\*(?!=<)/g, '') : '';
          return (
            <Fragment key={optionKey}>
              <FormControlLabel
                {...labelProps}
                htmlFor={fieldId}
                control={
                  <Radio
                    id={fieldId}
                    sx={{
                      color: error ? theme.palette.error.main : undefined
                    }}
                    disabled={disabled}
                    checked={isChecked}
                  />
                }
                value={optionKey}
                label={option[labelKey]}
              />

              {(otherTextField && isOther && (isChecked || otherTextFieldHiddenSpace)) && (
                <div style={{
                  marginLeft: 32,
                  visibility: isChecked || !otherTextFieldHiddenSpace ? 'visible' : 'hidden',
                }}>
                  <Typography variant="body2">{otherTextLabel}{asterisk}</Typography>
                  <TextFieldElement
                    id="other_text"
                    name="other_text"
                    control={control}
                    required={isOther && isChecked}
                    disabled={isOther && !isChecked}
                    fullWidth
                  />
                </div>
              )}
            </Fragment>
          );
        })}
      </RadioGroup>
      {renderHelperText && <FormHelperText {...helperTextProps}>{renderHelperText}</FormHelperText>}
    </FormControl>
  );
});
RadioButtonGroup.displayName = 'RadioButtonGroup';

export default RadioButtonGroup;
